import { DSVRowArray } from 'd3-dsv';
import { create } from 'zustand';

import {
  getCsvPreview,
  getJsonlPreview,
} from '@/components/DatasetUploadPane/preview';

import { getSuggestedKeysFromJsonlRow } from '../utils';

interface FileUploadTemplateState {
  file: File | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: 'text/csv' | 'application/jsonlines' | null;
  filePreview: DSVRowArray<string> | undefined;

  inputKeys: string[] | null;
  outputKeys: string[] | null;
  metadataKeys: string[] | null;
  setInputKeys: (keys: string[] | null) => void;
  setOutputKeys: (keys: string[] | null) => void;
  setMetadataKeys: (keys: string[] | null) => void;

  inputKeyMappings: Record<string, string>;
  outputKeyMappings: Record<string, string>;
  metadataKeyMappings: Record<string, string>;
  setInputKeyMapping: (originalKey: string, newKey: string | null) => void;
  setOutputKeyMapping: (originalKey: string, newKey: string | null) => void;
  setMetadataKeyMapping: (originalKey: string, newKey: string | null) => void;
  removeInputKeyMapping: (originalKey: string) => void;
  removeOutputKeyMapping: (originalKey: string) => void;
  removeMetadataKeyMapping: (originalKey: string) => void;

  error: string | null;
  setError: (error: string | null) => void;

  onCancel: () => void;
  handleCSVFile: (file: File) => Promise<void>;
  handleJSONLFile: (file: File) => Promise<void>;
}

export const useFileUploadTemplateStore = create<FileUploadTemplateState>(
  (set) => {
    return {
      file: null,
      fileName: null,
      fileSize: null,
      fileMimeType: null,
      filePreview: undefined,
      inputKeys: null,
      outputKeys: null,
      metadataKeys: null,
      inputKeyMappings: {},
      outputKeyMappings: {},
      metadataKeyMappings: {},
      error: null,
      setError: (mError) => set(() => ({ error: mError })),
      handleCSVFile: async (file: File) => {
        set({ error: null });
        set({
          file,
          fileName: file.name,
          fileSize: file.size,
          fileMimeType: 'text/csv',
        });
        const preview = await getCsvPreview(file);
        if (preview && preview.length > 0) {
          set({ filePreview: preview });
          set({ inputKeys: [preview.columns[0]] });
          if (preview.columns.length > 1) {
            set({ outputKeys: [preview.columns[1]] });
          }
        } else {
          set({
            error: 'File must contain a header row and at least one body row',
          });
        }
      },
      handleJSONLFile: async (file: File) => {
        set({ error: null });
        set({
          file,
          fileName: file.name,
          fileSize: file.size,
          fileMimeType: 'application/jsonlines',
        });
        try {
          const preview = await getJsonlPreview(file);
          if (preview && preview.length > 0) {
            const sampleRow = preview[0];
            const { inputKeys, outputKeys } =
              getSuggestedKeysFromJsonlRow(sampleRow);
            set({ filePreview: preview });
            set({ inputKeys });
            set({ outputKeys });
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          set({ error: e.message });
        }
      },
      onCancel: () => {
        set({
          file: null,
          fileName: null,
          fileSize: null,
          fileMimeType: null,
          filePreview: undefined,
          inputKeys: null,
          outputKeys: null,
          metadataKeys: null,
          inputKeyMappings: {},
          outputKeyMappings: {},
          metadataKeyMappings: {},
          error: null,
        });
      },
      setInputKeys: (keys) => set(() => ({ inputKeys: keys })),
      setOutputKeys: (keys) => set(() => ({ outputKeys: keys })),
      setMetadataKeys: (keys) => set(() => ({ metadataKeys: keys })),
      setInputKeyMapping: (originalKey, newKey) =>
        set((state) => {
          if (!newKey || !newKey.trim()) {
            const { [originalKey]: _, ...rest } = state.inputKeyMappings;
            return { inputKeyMappings: rest };
          }
          return {
            inputKeyMappings: {
              ...state.inputKeyMappings,
              [originalKey]: newKey,
            },
          };
        }),
      setOutputKeyMapping: (originalKey, newKey) =>
        set((state) => {
          if (!newKey || !newKey.trim()) {
            const { [originalKey]: _, ...rest } = state.outputKeyMappings;
            return { outputKeyMappings: rest };
          }
          return {
            outputKeyMappings: {
              ...state.outputKeyMappings,
              [originalKey]: newKey,
            },
          };
        }),
      setMetadataKeyMapping: (originalKey, newKey) =>
        set((state) => {
          if (!newKey || !newKey.trim()) {
            const { [originalKey]: _, ...rest } = state.metadataKeyMappings;
            return { metadataKeyMappings: rest };
          }
          return {
            metadataKeyMappings: {
              ...state.metadataKeyMappings,
              [originalKey]: newKey,
            },
          };
        }),
      removeInputKeyMapping: (originalKey) =>
        set((state) => {
          const { [originalKey]: _, ...rest } = state.inputKeyMappings;
          return { inputKeyMappings: rest };
        }),
      removeOutputKeyMapping: (originalKey) =>
        set((state) => {
          const { [originalKey]: _, ...rest } = state.outputKeyMappings;
          return { outputKeyMappings: rest };
        }),
      removeMetadataKeyMapping: (originalKey) =>
        set((state) => {
          const { [originalKey]: _, ...rest } = state.metadataKeyMappings;
          return { metadataKeyMappings: rest };
        }),
    };
  }
);
